import { useEffect, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react';
import type { Video } from '@/mocks/videos';

interface VideoPlayerProps {
  video: Video;
}

type Status = 'idle' | 'loading' | 'playing' | 'paused' | 'error';

export default function VideoPlayer({ video }: VideoPlayerProps) {
  const [status, setStatus] = useState<Status>('idle');
  const [progress, setProgress] = useState(0);
  const [coverFailed, setCoverFailed] = useState(false);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    setStatus('idle');
    setProgress(0);
    setCoverFailed(false);
  }, [video.id]);

  useEffect(() => {
    if (status !== 'playing') {
      if (timerRef.current) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
      return;
    }
    timerRef.current = window.setInterval(() => {
      setProgress((prev) => (prev >= 100 ? 0 : prev + 0.4));
    }, 100);
    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
    };
  }, [status]);

  const handlePlay = () => {
    if (coverFailed) {
      setStatus('error');
      return;
    }
    setStatus('loading');
    window.setTimeout(() => {
      setStatus('playing');
    }, 1400);
  };

  const togglePlayPause = () => {
    if (status === 'playing') setStatus('paused');
    else if (status === 'paused') setStatus('playing');
    else handlePlay();
  };

  const handleSeek = (event: ReactMouseEvent<HTMLDivElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const ratio = (event.clientX - rect.left) / rect.width;
    setProgress(Math.min(100, Math.max(0, ratio * 100)));
  };

  const formatTime = (ratio: number) => {
    const total = 90 * 60; // demo total seconds
    const seconds = Math.floor((ratio / 100) * total);
    const mm = String(Math.floor(seconds / 60)).padStart(2, '0');
    const ss = String(seconds % 60).padStart(2, '0');
    return `${mm}:${ss}`;
  };

  return (
    <div className="w-full">
      <div className="relative aspect-video w-full overflow-hidden rounded-lg bg-foreground-950">
        {status === 'error' ? (
          <div className="flex h-full w-full flex-col items-center justify-center px-6 text-center">
            <i className="ri-error-warning-line text-5xl text-accent-400" aria-hidden="true" />
            <p className="mt-4 text-base font-medium text-background-50">
              视频加载失败
            </p>
            <p className="mt-2 text-sm text-background-300">
              请检查网络后刷新页面重试。
            </p>
            <button
              type="button"
              onClick={() => {
                setCoverFailed(false);
                handlePlay();
              }}
              className="mt-5 inline-flex cursor-pointer items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-refresh-line" aria-hidden="true" />
              重新加载
            </button>
          </div>
        ) : (
          <>
            <img
              src={video.cover}
              alt={`${video.title} 播放封面`}
              title={`${video.title} - 苹果视频`}
              onError={() => {
                setCoverFailed(true);
                setStatus('error');
              }}
              className={`h-full w-full object-top transition-all duration-700 ${
                status === 'playing' ? 'scale-105 opacity-70' : 'opacity-100'
              }`}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-foreground-950/20 to-foreground-950/30" />

            {status === 'idle' && (
              <button
                type="button"
                onClick={handlePlay}
                aria-label="播放"
                className="absolute inset-0 m-auto flex h-16 w-16 cursor-pointer items-center justify-center rounded-full bg-background-50/90 text-primary-600 transition-transform hover:scale-110 md:h-20 md:w-20"
              >
                <i className="ri-play-fill text-3xl md:text-4xl" aria-hidden="true" />
              </button>
            )}

            {status === 'loading' && (
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="h-10 w-10 animate-spin rounded-full border-2 border-background-50/30 border-t-background-50" />
                <p className="mt-4 text-sm text-background-100">视频加载中…</p>
              </div>
            )}

            {(status === 'playing' || status === 'paused') && (
              <div className="absolute inset-0 flex items-center justify-center">
                {status === 'paused' && (
                  <button
                    type="button"
                    onClick={togglePlayPause}
                    aria-label="继续播放"
                    className="flex h-16 w-16 cursor-pointer items-center justify-center rounded-full bg-background-50/90 text-primary-600 transition-transform hover:scale-110"
                  >
                    <i className="ri-play-fill text-3xl" aria-hidden="true" />
                  </button>
                )}
                {status === 'playing' && (
                  <div className="flex items-end gap-1">
                    {[0, 1, 2, 3, 4].map((bar) => (
                      <span
                        key={bar}
                        className="w-1.5 rounded-full bg-accent-400 mv-animate-pulse"
                        style={{ height: `${12 + ((bar * 7) % 26)}px` }}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            {(status === 'playing' || status === 'paused') && (
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-foreground-950/90 to-transparent px-4 pb-4 pt-10">
                <div
                  className="group mb-3 h-1.5 w-full cursor-pointer rounded-full bg-background-50/25"
                  onClick={handleSeek}
                >
                  <div
                    className="relative h-full rounded-full bg-accent-500 transition-[width] duration-150"
                    style={{ width: `${progress}%` }}
                  >
                    <span className="absolute -right-1.5 top-1/2 h-3 w-3 -translate-y-1/2 rounded-full bg-background-50 opacity-0 transition-opacity group-hover:opacity-100" />
                  </div>
                </div>
                <div className="flex items-center gap-4 text-background-50">
                  <button
                    type="button"
                    onClick={togglePlayPause}
                    aria-label={status === 'playing' ? '暂停' : '播放'}
                    className="cursor-pointer text-xl"
                  >
                    <i
                      className={status === 'playing' ? 'ri-pause-fill' : 'ri-play-fill'}
                      aria-hidden="true"
                    />
                  </button>
                  <span className="text-xs tabular-nums">
                    {formatTime(progress)} / {video.duration}
                  </span>
                  <div className="ml-auto flex items-center gap-3">
                    <i className="ri-volume-up-line text-lg" aria-hidden="true" />
                    <i className="ri-fullscreen-line text-lg" aria-hidden="true" />
                  </div>
                </div>
              </div>
            )}

            <span className="absolute left-3 top-3 rounded bg-foreground-950/60 px-2 py-1 text-[11px] text-background-100 backdrop-blur-sm md:left-4 md:top-4">
              演示播放界面 · 无真实视频源
            </span>
          </>
        )}
      </div>
    </div>
  );
}